import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { dashboardAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
    Users, 
    ClipboardList, 
    PoundSterling, 
    TrendingUp,
    Plus,
    ChevronRight,
    FileText,
    Clock,
    CheckCircle2,
    Send,
    ArrowRight,
    Receipt
} from 'lucide-react';

const statusColors = {
    Draft: 'bg-slate-100 text-slate-700',
    Sent: 'bg-blue-100 text-blue-700',
    Accepted: 'bg-green-100 text-green-700',
    Completed: 'bg-emerald-100 text-emerald-700',
    Lost: 'bg-red-100 text-red-700'
};

const StatusIcon = ({ status }) => {
    switch (status) {
        case 'Draft': return <FileText className="h-4 w-4" />;
        case 'Sent': return <Send className="h-4 w-4" />;
        case 'Accepted':
        case 'Completed': return <CheckCircle2 className="h-4 w-4" />;
        default: return <Clock className="h-4 w-4" />;
    }
};

export const Dashboard = () => {
    const { user, isAdmin } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');

    const DATE_FILTERS = [
        { key: 'all',    label: 'All Time' },
        { key: 'month',  label: 'This Month' },
        { key: '3month', label: 'Last 3 Months' },
        { key: 'year',   label: 'This Year' },
        { key: 'custom', label: 'Custom' },
    ];

    const getDateParams = (filter) => {
        const now = new Date();
        if (filter === 'all') return {};
        if (filter === 'custom') {
            const params = {};
            if (customFrom) params.from_date = new Date(customFrom).toISOString();
            if (customTo) {
                // Include the full end day
                const end = new Date(customTo);
                end.setHours(23, 59, 59, 999);
                params.to_date = end.toISOString();
            }
            return params;
        }
        let from;
        if (filter === 'month') {
            from = new Date(now.getFullYear(), now.getMonth(), 1);
        } else if (filter === '3month') {
            from = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        } else if (filter === 'year') {
            from = new Date(now.getFullYear(), 0, 1);
        }
        return { from_date: from.toISOString(), to_date: now.toISOString() };
    };

    useEffect(() => {
        // Don't auto-fetch for custom until user explicitly applies
        if (dateFilter !== 'custom') fetchStats();
    }, [dateFilter]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getStats(getDateParams(dateFilter));
            setStats(response.data);
        } catch {
            toast.error('Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value || 0);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-32 rounded-xl" />
                    <Skeleton className="h-48 rounded-xl" />
                </div>
            </Layout>
        );
    }

    // ── SURVEYOR VIEW ──────────────────────────────────────────────────────────
    if (!isAdmin()) {
        return (
            <Layout>
                <div className="space-y-5">
                    {/* Welcome */}
                    <div>
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            Hi, {user?.name?.split(' ')[0] || 'there'}
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">Ready to start a survey?</p>
                    </div>

                    {/* Primary CTA */}
                    <Link to="/jobs/new">
                        <Card className="bg-red-600 border-red-700 shadow-md hover:bg-red-700 transition-colors cursor-pointer">
                            <CardContent className="p-5 flex items-center justify-between">
                                <div>
                                    <p className="font-heading text-xl font-bold text-white uppercase tracking-wide">
                                        New Survey
                                    </p>
                                    <p className="text-red-200 text-sm mt-0.5">Create a new job & start surveying</p>
                                </div>
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <Plus className="h-6 w-6 text-white" />
                                </div>
                            </CardContent>
                        </Card>
                    </Link>

                    {/* Quick stats strip */}
                    <div className="grid grid-cols-3 gap-2">
                        <Card className="bg-white border-slate-200">
                            <CardContent className="p-3 text-center">
                                <p className="text-xl font-bold text-slate-900">{stats?.jobs_by_status?.draft || 0}</p>
                                <p className="text-[10px] text-slate-500 uppercase font-semibold mt-0.5">In Draft</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-slate-200">
                            <CardContent className="p-3 text-center">
                                <p className="text-xl font-bold text-blue-700">{stats?.jobs_by_status?.sent || 0}</p>
                                <p className="text-[10px] text-blue-600 uppercase font-semibold mt-0.5">Sent</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-slate-200">
                            <CardContent className="p-3 text-center">
                                <p className="text-xl font-bold text-green-700">{stats?.jobs_by_status?.accepted || 0}</p>
                                <p className="text-[10px] text-green-600 uppercase font-semibold mt-0.5">Accepted</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Recent jobs */}
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardHeader className="pb-2 pt-4 px-4">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-heading text-base uppercase tracking-tight text-slate-900">
                                    Recent Jobs
                                </CardTitle>
                                <Link to="/jobs" className="text-xs text-red-600 font-semibold flex items-center gap-1 hover:underline">
                                    All jobs <ArrowRight className="h-3 w-3" />
                                </Link>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {stats?.recent_jobs?.length > 0 ? (
                                <div className="divide-y divide-slate-100">
                                    {stats.recent_jobs.map((job) => (
                                        <Link
                                            key={job.id}
                                            to={`/jobs/${job.id}`}
                                            className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${statusColors[job.status] || 'bg-slate-100'}`}>
                                                    <StatusIcon status={job.status} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-sm text-slate-900 truncate">{job.quote_number}</p>
                                                    <p className="text-xs text-slate-500 truncate">{job.customer_name}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <div className="text-right">
                                                    <p className="font-semibold text-sm text-slate-900">
                                                        {formatCurrency(job.pricing_summary?.total)}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400">{formatDate(job.created_at)}</p>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-slate-400" />
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center">
                                    <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <ClipboardList className="h-7 w-7 text-slate-400" />
                                    </div>
                                    <p className="text-slate-600 font-medium">No jobs yet</p>
                                    <p className="text-slate-400 text-sm mt-1">Tap New Survey above to get started</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    // ── ADMIN VIEW ─────────────────────────────────────────────────────────────
    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl lg:text-3xl font-bold uppercase tracking-tight text-slate-900">
                            Dashboard
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">Your business at a glance</p>
                    </div>
                    <Link to="/jobs/new">
                        <Button className="h-12 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide shadow-sm" data-testid="new-job-btn">
                            <Plus className="h-5 w-5 mr-2" />
                            <span className="hidden sm:inline">New Job</span>
                        </Button>
                    </Link>
                </div>

                {/* Date Filter */}
                <div className="space-y-2">
                    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="date-filter">
                        {DATE_FILTERS.map(f => (
                            <button
                                key={f.key}
                                onClick={() => setDateFilter(f.key)}
                                data-testid={`date-filter-${f.key}`}
                                className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                                    dateFilter === f.key
                                        ? 'bg-red-600 text-white border-red-600'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    {/* Custom date range inputs */}
                    {dateFilter === 'custom' && (
                        <div className="flex items-center gap-2 pt-1">
                            <input
                                type="date"
                                value={customFrom}
                                onChange={e => setCustomFrom(e.target.value)}
                                className="flex-1 h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white"
                                data-testid="custom-from-date"
                            />
                            <span className="text-slate-400 text-sm shrink-0">to</span>
                            <input
                                type="date"
                                value={customTo}
                                onChange={e => setCustomTo(e.target.value)}
                                className="flex-1 h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white"
                                data-testid="custom-to-date"
                            />
                            <button
                                onClick={fetchStats}
                                disabled={!customFrom && !customTo}
                                className="shrink-0 h-10 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors"
                                data-testid="apply-custom-date"
                            >
                                Apply
                            </button>
                        </div>
                    )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                    <Users className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-900">{stats?.total_customers || 0}</p>
                                    <p className="text-xs text-slate-500 uppercase font-medium">Customers</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                                    <ClipboardList className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-900">{stats?.total_jobs || 0}</p>
                                    <p className="text-xs text-slate-500 uppercase font-medium">
                                        {dateFilter === 'all' ? 'Total Jobs' : 'Jobs'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                                    <PoundSterling className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold text-slate-900">{formatCurrency(stats?.total_quoted_value)}</p>
                                    <p className="text-xs text-slate-500 uppercase font-medium">Quoted</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white border-slate-200 shadow-sm">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                                    <Receipt className="h-5 w-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold text-emerald-700">{formatCurrency(stats?.total_invoiced_value)}</p>
                                    <p className="text-xs text-slate-500 uppercase font-medium">Invoiced</p>
                                    {stats?.paid_total > 0 && (
                                        <p className="text-[10px] text-emerald-600 font-medium mt-0.5">
                                            {formatCurrency(stats.paid_total)} paid
                                        </p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Job Pipeline */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center justify-between">
                            Job Pipeline
                            <Link to="/jobs" className="text-sm text-red-600 font-normal normal-case hover:underline">View all</Link>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex gap-2 overflow-x-auto pb-2">
                            <div className="flex-1 min-w-[80px] text-center p-3 bg-slate-50 rounded-lg">
                                <p className="text-xl font-bold text-slate-700">{stats?.jobs_by_status?.draft || 0}</p>
                                <p className="text-xs text-slate-500 uppercase">Draft</p>
                            </div>
                            <div className="flex-1 min-w-[80px] text-center p-3 bg-blue-50 rounded-lg">
                                <p className="text-xl font-bold text-blue-700">{stats?.jobs_by_status?.sent || 0}</p>
                                <p className="text-xs text-blue-600 uppercase">Sent</p>
                            </div>
                            <div className="flex-1 min-w-[80px] text-center p-3 bg-green-50 rounded-lg">
                                <p className="text-xl font-bold text-green-700">{stats?.jobs_by_status?.accepted || 0}</p>
                                <p className="text-xs text-green-600 uppercase">Accepted</p>
                            </div>
                            <div className="flex-1 min-w-[80px] text-center p-3 bg-emerald-50 rounded-lg">
                                <p className="text-xl font-bold text-emerald-700">{stats?.jobs_by_status?.completed || 0}</p>
                                <p className="text-xs text-emerald-600 uppercase">Done</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Enquiry Sources */}
                <Card className="bg-white border-slate-200 shadow-sm" data-testid="enquiry-sources-panel">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight">Enquiry Sources</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.enquiry_sources?.length > 0 ? (
                            <div className="space-y-2">
                                {stats.enquiry_sources.map((s) => (
                                    <div key={s.source} className="flex items-center justify-between" data-testid={`source-${s.source}`}>
                                        <span className="text-sm font-medium text-slate-700 truncate">{s.source}</span>
                                        <div className="flex items-center gap-3">
                                            <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.min(s.percentage, 100)}%` }} />
                                            </div>
                                            <span className="text-sm font-semibold text-slate-700 tabular-nums w-8 text-right">{s.count}</span>
                                            <span className="text-xs text-slate-500 tabular-nums w-12 text-right">{s.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-400 text-center">No data yet</p>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Jobs */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight">Recent Jobs</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {stats?.recent_jobs?.length > 0 ? (
                            <div className="divide-y divide-slate-100">
                                {stats.recent_jobs.map((job) => (
                                    <Link key={job.id} to={`/jobs/${job.id}`}
                                        className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                                        data-testid={`job-${job.quote_number}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusColors[job.status] || 'bg-slate-100'}`}>
                                                <StatusIcon status={job.status} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-slate-900 truncate">{job.quote_number}</p>
                                                <p className="text-sm text-slate-500 truncate">{job.customer_name}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <p className="font-semibold text-slate-900">{formatCurrency(job.pricing_summary?.total)}</p>
                                                <p className="text-xs text-slate-500">{formatDate(job.created_at)}</p>
                                            </div>
                                            <ChevronRight className="h-5 w-5 text-slate-400" />
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <ClipboardList className="h-8 w-8 text-slate-400" />
                                </div>
                                <p className="text-slate-600 font-medium">No jobs yet</p>
                                <p className="text-slate-500 text-sm mt-1 mb-4">Start your first survey to get going</p>
                                <Link to="/jobs/new">
                                    <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="empty-new-job-btn">
                                        <Plus className="h-4 w-4 mr-2" /> Create First Job
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>

            </div>
        </Layout>
    );
};
